import type {EffectivePasswordPolicy} from '@/services/password-policy-services'

// Kinds of policy violation the client mirrors. Self-reference rules (username, name, organization
// name, blocklist, HIBP, strength) are not mirrored here because the effective-policy response
// either does not expose them or they need zxcvbn-ts to evaluate; the server remains authoritative
// for those.
export type PasswordPolicyViolationKind =
    | 'min-length'
    | 'requires-digit'
    | 'requires-special-char'
    | 'min-distinct-chars'

export interface PasswordPolicyViolation {
    kind: PasswordPolicyViolationKind
    minLength?: number
    minDistinctChars?: number
}

// Matches PasswordValidationService#isSpecialChar on the backend: anything that is not a Unicode
// letter, not a digit, and not whitespace. The unicode flag keeps the class consistent with
// `Char::isLetterOrDigit` for non-ASCII alphabets.
const SPECIAL_CHAR_PATTERN = /[^\p{L}\p{N}\s]/u

// Count distinct UTF-16 code units, mirroring Kotlin's `String.toSet()` (the backend uses
// `Set<Char>` which iterates UTF-16 Chars). `new Set(password)` would iterate Unicode code points
// instead, so non-BMP characters like emoji surrogate pairs would count as 1 here but as 2 on the
// server — making the client-side gate reject passwords the server would happily accept (#679).
function countDistinctCharacters(password: string): number {
    const codeUnits = new Set<number>()
    for (let i = 0; i < password.length; i += 1) {
        codeUnits.add(password.charCodeAt(i))
    }
    return codeUnits.size
}

export function collectPasswordPolicyViolations(
    password: string,
    policy: EffectivePasswordPolicy,
): PasswordPolicyViolation[] {
    const violations: PasswordPolicyViolation[] = []

    if (password.length < policy.minLength) {
        violations.push({kind: 'min-length', minLength: policy.minLength})
    }
    if (policy.minDistinctChars > 0 && countDistinctCharacters(password) < policy.minDistinctChars) {
        violations.push({kind: 'min-distinct-chars', minDistinctChars: policy.minDistinctChars})
    }
    if (policy.requireDigit && !/\d/.test(password)) {
        violations.push({kind: 'requires-digit'})
    }
    if (policy.requireSpecialChar && !SPECIAL_CHAR_PATTERN.test(password)) {
        violations.push({kind: 'requires-special-char'})
    }

    return violations
}
